import { BaseFeature } from '../../core/base-feature.js';

/**
 * SoundBlasterFeature - Frontend
 *
 * Controls the Sound Blaster punishment feature on the desktop.
 * Plays unstoppable sounds, streams mic audio, schedules sounds,
 * and triggers sounds from timeline events.
 */
export class SoundBlasterFeature extends BaseFeature {
    constructor(coreAPI) {
        super(coreAPI);

        this._pendingRequests = new Map(); // requestId -> resolve
        this._requestCounter = 0;
        this._stateSubscribers = new Set();
        this._micStream = null;
        this._micProcessor = null;
        this._micContext = null;

        this._state = {
            globalVolume: 80,
            activeSounds: [],
            scheduledSounds: [],
            triggerRules: [],
            micStreaming: false,
        };
    }

    static getMetadata() {
        return {
            id: 'sound-blaster',
            name: 'Sound Blaster',
            version: 'v1.0',
            capabilities: [
                'play', 'stop', 'stop_all', 'set_volume', 'set_global_volume',
                'get_library', 'get_state', 'schedule', 'cancel_schedule',
                'add_trigger', 'remove_trigger', 'get_triggers', 'play_tone',
                'mic_stream_start', 'mic_stream_stop'
            ]
        };
    }

    async initialize() {
        console.log('[SoundBlaster] Initialized');
    }

    // ─── Message Handling ────────────────────────────────────────────────────

    async handleMessage(message) {
        const { action, cmd, payload } = message;
        const command = cmd || action;

        // State updates pushed from desktop (not a response to a request)
        if (command === 'state_update') {
            this._state = { ...this._state, ...payload };
            this._notifyStateSubscribers(this._state);
            return;
        }

        // Responses to our commands: e.g. 'play_response', 'get_state_response'
        if (command.endsWith('_response')) {
            const requestId = payload?._requestId;
            if (requestId && this._pendingRequests.has(requestId)) {
                const resolve = this._pendingRequests.get(requestId);
                this._pendingRequests.delete(requestId);
                resolve(payload);
            } else {
                // Fallback: resolve by action name for backwards compat
                const requestType = command.replace('_response', '');
                const resolve = this._pendingRequests.get(requestType);
                if (resolve) {
                    this._pendingRequests.delete(requestType);
                    resolve(payload);
                }
            }
            return;
        }
    }

    // ─── Request Helper ──────────────────────────────────────────────────────

    _request(action, payload = {}) {
        return new Promise((resolve) => {
            const requestId = `req_${++this._requestCounter}_${Date.now()}`;
            this.sendMessage(action, { ...payload, _requestId: requestId });
            this._pendingRequests.set(requestId, resolve);

            // Timeout after 10s so promises don't hang forever
            setTimeout(() => {
                if (this._pendingRequests.has(requestId)) {
                    this._pendingRequests.delete(requestId);
                    resolve(null);
                }
            }, 10000);
        });
    }

    // ─── Playback Controls ───────────────────────────────────────────────────

    async playSound(soundId, options = {}) {
        return this._request('play', {
            soundId,
            volume: options.volume,
            loop: options.loop !== undefined ? options.loop : true,
            duration: options.duration || 0,
            subliminal: options.subliminal || false,
            pan: options.pan
        });
    }

    async playTone(frequency, options = {}) {
        return this._request('play_tone', {
            frequency,
            waveform: options.waveform || 'sine',
            volume: options.volume,
            loop: options.loop !== undefined ? options.loop : true,
            duration: options.duration || 0
        });
    }

    async stopSound(instanceId) {
        return this._request('stop', { instanceId });
    }

    async stopAll() {
        return this._request('stop_all');
    }

    async setVolume(instanceId, volume) {
        return this._request('set_volume', { instanceId, volume });
    }

    async setGlobalVolume(volume) {
        return this._request('set_global_volume', { volume });
    }

    async getLibrary() {
        const result = await this._request('get_library');
        if (result?.library) {
            this._library = result.library;
        }
        return result?.library || {};
    }

    async getState() {
        const result = await this._request('get_state');
        if (result) {
            this._state = { ...this._state, ...result };
            this._notifyStateSubscribers(this._state);
        }
        return result;
    }

    // ─── Scheduling ──────────────────────────────────────────────────────────

    async scheduleSound(soundId, triggerAt, options = {}) {
        return this._request('schedule', {
            soundId,
            triggerAt,
            volume: options.volume,
            loop: options.loop,
            duration: options.duration,
            repeat: options.repeat
        });
    }

    async cancelSchedule(scheduleId) {
        return this._request('cancel_schedule', { scheduleId });
    }

    // ─── Timeline Triggers ───────────────────────────────────────────────────

    async addTrigger(eventPattern, soundId, options = {}) {
        return this._request('add_trigger', {
            eventPattern,
            soundId,
            volume: options.volume,
            loop: options.loop,
            duration: options.duration,
            cooldownMs: options.cooldownMs,
            maxConcurrent: options.maxConcurrent
        });
    }

    async removeTrigger(triggerId) {
        return this._request('remove_trigger', { triggerId });
    }

    // ─── Regeneration & Custom ────────────────────────────────────────────────

    async regenerateSound(soundId) {
        return this._request('regenerate', { soundId });
    }

    async uploadCustomSound(name, category, description, audioBase64, mimeType) {
        return this._request('upload_custom', { name, category, description, audioBase64, mimeType });
    }

    async deleteCustomSound(soundId) {
        return this._request('delete_custom', { soundId });
    }

    // ─── Spatial Audio ────────────────────────────────────────────────────────

    async setPan(instanceId, pan) {
        return this._request('set_pan', { instanceId, pan });
    }

    async sweepPan(instanceId, options) {
        return this._request('sweep_pan', { instanceId, ...options });
    }

    // ─── Subliminal ───────────────────────────────────────────────────────────

    async stopAllSubliminal() {
        return this._request('stop_all_subliminal');
    }

    // ─── Phantom ──────────────────────────────────────────────────────────────

    async setPhantom(config) {
        return this._request('set_phantom', config);
    }

    async getPhantom() {
        return this._request('get_phantom');
    }

    // ─── Soundscape ───────────────────────────────────────────────────────────

    async setSoundscape(config) {
        return this._request('set_soundscape', config);
    }

    async getSoundscape() {
        return this._request('get_soundscape');
    }

    // ─── Microphone Streaming ────────────────────────────────────────────────

    async startMicStream() {
        try {
            this._micStream = await navigator.mediaDevices.getUserMedia({ audio: true });
            this._micContext = new AudioContext({ sampleRate: 44100 });
            const source = this._micContext.createMediaStreamSource(this._micStream);

            this._micProcessor = this._micContext.createScriptProcessor(4096, 1, 1);
            this._micProcessor.onaudioprocess = (e) => {
                const inputData = e.inputBuffer.getChannelData(0);
                this.sendMessage('mic_audio_data', {
                    audioData: Array.from(inputData)
                });
            };

            source.connect(this._micProcessor);
            this._micProcessor.connect(this._micContext.destination);

            await this._request('mic_stream_start');
            this._state.micStreaming = true;
            this._notifyStateSubscribers(this._state);

            return { success: true };
        } catch (err) {
            return { success: false, error: err.message };
        }
    }

    async stopMicStream() {
        if (this._micProcessor) {
            this._micProcessor.disconnect();
            this._micProcessor = null;
        }
        if (this._micContext) {
            this._micContext.close();
            this._micContext = null;
        }
        if (this._micStream) {
            this._micStream.getTracks().forEach(t => t.stop());
            this._micStream = null;
        }

        await this._request('mic_stream_stop');
        this._state.micStreaming = false;
        this._notifyStateSubscribers(this._state);

        return { success: true };
    }

    // ─── State Subscription (for React) ──────────────────────────────────────

    subscribe(event, callback) {
        if (event === 'state_update') {
            this._stateSubscribers.add(callback);
            return () => this._stateSubscribers.delete(callback);
        }
        return () => {};
    }

    _notifyStateSubscribers(data) {
        for (const cb of this._stateSubscribers) {
            try { cb(data); } catch (e) { /* ignore */ }
        }
    }

    // ─── Cleanup ─────────────────────────────────────────────────────────────

    async cleanup() {
        if (this._micProcessor) this._micProcessor.disconnect();
        if (this._micContext) this._micContext.close();
        if (this._micStream) this._micStream.getTracks().forEach(t => t.stop());
        this._pendingRequests.clear();
        this._stateSubscribers.clear();
    }
}
